import { computed, linkedSignal, Signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { Page } from '../../core/models/page.model';
import { pageState, type PagedList } from './paged-list';
import { compactParams, QueryParams, sameParams } from './query-params';

export interface PagedResource<T> extends PagedList<T> {
  readonly loading: Signal<boolean>;
  readonly error: Signal<unknown>;
  reloadFirstPage(): void;
}

export interface PagedResourceOptions {
  /** Rows per page until the user picks another size. Defaults to 10. */
  readonly size?: number;
  /**
   * The filters sent with every page. Every signal read here is tracked: when the resulting params
   * change, the list goes back to the first page and reloads. `null` and `undefined` are not sent.
   */
  readonly params?: () => QueryParams;
}

const NO_PARAMS: () => QueryParams = () => ({});

/** Must be called in an injection context, i.e. when initialising a component field. */
export function pagedResource<T>(url: string, options: PagedResourceOptions = {}): PagedResource<T> {
  const readParams = options.params ?? NO_PARAMS;

  // Compared by value: re-reading the same filters is not a change and must not move the page.
  const filters = computed(() => compactParams(readParams()), { equal: sameParams });
  // Other filters are another result set, and page 7 of the old one may not exist in it.
  const state = pageState(filters, options.size ?? 10);
  const { page, size } = state;

  const resource = httpResource<Page<T>>(() => ({
    url,
    params: { ...filters(), page: page(), size: size() }
  }));

  // value() throws while the resource is in error; the error is reported through `error` instead.
  const current = computed(() => (resource.hasValue() ? resource.value() : undefined));
  // The last total that arrived: while a page loads or after it fails, the paginator keeps the pages to go back to.
  const totalRecords = linkedSignal<Page<T> | undefined, number>({
    source: current,
    computation: (loaded, previous) => loaded?.page.totalElements ?? previous?.value ?? 0
  });

  return {
    ...state,
    items: computed(() => current()?.content ?? []),
    totalRecords,
    loading: resource.isLoading,
    error: resource.error,
    reloadFirstPage() {
      if (page() === 0) {
        resource.reload();
      } else {
        page.set(0);
      }
    }
  };
}
